export interface InsuranceProduct {
  id: string;
  name: string;
  category: "Term Life" | "Health & Medical" | "Wealth & ULIP" | "Asset & Motor";
  tagline: string;
  defaultSumAssuredINR: number;
  defaultSumAssuredUSD: number;
  indicativeAnnualPremiumINR: number;
  indicativeAnnualPremiumUSD: number;
  tenureOptions: string[];
  keyBenefits: string[];
  taxBenefit: "Section 80C" | "Section 80D" | "Section 10(10D)" | "GST Exempt";
  claimRatio: string;
  badge?: string;
}

export const INSURANCE_PRODUCTS: InsuranceProduct[] = [
  {
    id: "aegis-term-shield",
    name: "Aegis Term Shield Pure Life & Critical Illness",
    category: "Term Life",
    tagline: "Uncompromised financial security for your family with 64 critical illness coverages and accelerated terminal illness payouts.",
    defaultSumAssuredINR: 20000000, // 2 Cr
    defaultSumAssuredUSD: 250000,
    indicativeAnnualPremiumINR: 18500,
    indicativeAnnualPremiumUSD: 225,
    tenureOptions: ["30 Years (Up to Age 65)", "40 Years (Up to Age 75)", "Whole Life (Up to Age 99)"],
    keyBenefits: [
      "100% Tax-free lump sum payout to nominee",
      "64 critical illnesses covered with instant advance",
      "Waiver of premium on accidental total disability",
      "Immediate terminal illness 100% claim settlement",
    ],
    taxBenefit: "Section 80C",
    claimRatio: "99.2% Claim Settlement Ratio",
    badge: "MOST POPULAR",
  },
  {
    id: "optima-family-health",
    name: "Optima Global Family Health Super Top-Up",
    category: "Health & Medical",
    tagline: "Comprehensive cashless hospitalization worldwide with zero room rent capping, robotic surgeries, and OPD wellness allowances.",
    defaultSumAssuredINR: 10000000, // 1 Cr
    defaultSumAssuredUSD: 125000,
    indicativeAnnualPremiumINR: 24800,
    indicativeAnnualPremiumUSD: 300,
    tenureOptions: ["1 Year Annual Renewal", "2 Years Multi-Year Lock", "3 Years Multi-Year Lock"],
    keyBenefits: [
      "14,000+ cashless network hospitals across India & overseas",
      "Zero room rent restrictions (Single Private Suite)",
      "Pre & post-hospitalization medical coverage for 180 days",
      "No-Claim Bonus up to 100% sum assured enhancement",
    ],
    taxBenefit: "Section 80D",
    claimRatio: "15-Minute Cashless Approval",
    badge: "COMPREHENSIVE",
  },
  {
    id: "pinnacle-wealth-multiplier",
    name: "Pinnacle Wealth Builder & ULIP Multiplier",
    category: "Wealth & ULIP",
    tagline: "High-yield wealth creation engineered with institutional asset management, zero premium allocation charges, and life protection.",
    defaultSumAssuredINR: 5000000, // 50 Lakh
    defaultSumAssuredUSD: 60000,
    indicativeAnnualPremiumINR: 120000,
    indicativeAnnualPremiumUSD: 1450,
    tenureOptions: ["10 Years Horizon", "15 Years Horizon", "20 Years Horizon"],
    keyBenefits: [
      "Zero premium allocation & policy admin charges",
      "Unlimited free switches between Bluechip, Midcap & Debt funds",
      "Systematic partial withdrawals allowed post lock-in",
      "10x life cover protection throughout the accumulation period",
    ],
    taxBenefit: "Section 10(10D)",
    claimRatio: "Top-Quartile Fund Performance",
    badge: "WEALTH CREATION",
  },
  {
    id: "umbrella-executive-motor",
    name: "Umbrella Executive Motor & Asset Shield",
    category: "Asset & Motor",
    tagline: "Bumper-to-bumper protection for luxury vehicles and high-value family assets with engine safeguard and zero depreciation.",
    defaultSumAssuredINR: 3500000, // 35 Lakh IDV
    defaultSumAssuredUSD: 42000,
    indicativeAnnualPremiumINR: 42000,
    indicativeAnnualPremiumUSD: 510,
    tenureOptions: ["1 Year Comprehensive", "3 Years Bundled Coverage"],
    keyBenefits: [
      "Zero Depreciation on all fiber, glass, rubber and plastic parts",
      "Engine & gearbox protector against water ingression / hydrostatic lock",
      "24x7 pan-India breakdown concierge & courtesy luxury loaner car",
      "Return to Invoice (RTI) coverage against total loss or theft",
    ],
    taxBenefit: "GST Exempt",
    claimRatio: "Cashless Garage Settlement in 4 Hours",
  },
];

export const DIAGNOSTIC_LAB_PARTNERS = [
  {
    name: "Aegis Central Diagnostic Center - Indiranagar",
    address: "100-Ft Road, Indiranagar, Bangalore 560038",
    facility: "Full Body Executive Profile, ECG & TMT, High-Res Chest X-Ray",
  },
  {
    name: "Apollo Clinic & Diagnostic Hub - Koramangala",
    address: "80-Ft Road, Koramangala 4th Block, Bangalore 560034",
    facility: "Biometric Lipid/HbA1c Profile, Kidney & Liver Function Panels",
  },
  {
    name: "Metropolis Healthcare Tele-MER Center - MG Road",
    address: "Trinity Circle, MG Road, Bangalore 560001",
    facility: "Tele-Medical Examiner Review (MER) & Home Phlebotomy Kit",
  },
];

export const UNDERWRITING_DOCTORS = [
  {
    name: "Dr. Arvind Subramanian",
    title: "Chief Medical Underwriter",
    degrees: "MD (Internal Medicine), Fellow of the Academy of Life Underwriting (FALU)",
  },
  {
    name: "Dr. Shweta Kulkarni",
    title: "Senior Actuarial & Medical Examiner",
    degrees: "MBBS, DNB (Cardiology), FLMI Underwriting Specialist",
  },
];

// Interactive Premium Calculator Helper
export function calculateIndicativePremium({
  productId,
  age,
  sumAssured,
  isTobaccoUser,
  tenureYears,
  currency,
}: {
  productId: string;
  age: number;
  sumAssured: number;
  isTobaccoUser: boolean;
  tenureYears: number;
  currency: "INR" | "USD";
}): number {
  let baseRatePerThousand = 0.85;

  if (productId === "aegis-term-shield") {
    // Term Insurance calculation
    baseRatePerThousand = age < 30 ? 0.75 : age < 40 ? 1.15 : age < 50 ? 2.4 : 4.8;
    if (isTobaccoUser) baseRatePerThousand *= 1.55;
    const premium = (sumAssured / 1000) * baseRatePerThousand;
    return Math.round(currency === "USD" ? premium / 82 : premium);
  } else if (productId === "optima-family-health") {
    // Health Insurance calculation
    let base = age < 35 ? 18500 : age < 45 ? 24800 : age < 55 ? 36000 : 54000;
    if (sumAssured > 10000000) base *= 1.4;
    return Math.round(currency === "USD" ? base / 82 : base);
  } else if (productId === "pinnacle-wealth-multiplier") {
    // Wealth ULIP
    let base = currency === "USD" ? 1450 : 120000;
    return base;
  } else {
    // Motor & Asset
    let base = currency === "USD" ? 510 : 42000;
    return base;
  }
}
